import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ShieldCheck,
  FileText,
  Landmark,
  Printer,
  Download,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Gauge,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { GatewayCisoApi } from "../Api/gatewayApi";
import GatewayCisoDashboard from "../components/GatewayCisoDashboard";
import { apiError } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";

// Format a bare integer string with thousands separators; pass non-numeric strings through unchanged.
function fmtNum(v: any): string {
  if (v == null) return "—";
  const str = String(v);
  return /^\d+$/.test(str) ? Number(str).toLocaleString() : str;
}

function Loading({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin" /> {text}
    </div>
  );
}

function ErrorState({ what, msg }: { what: string; msg: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
      <p className="text-sm text-muted-foreground">Failed to load {what}: {msg}</p>
    </div>
  );
}

function EvidenceCell({ ev }: { ev: any }) {
  const m = /^(\d[\d,]*)\s+of\s+(\d[\d,]*)$/.exec(ev.value || "");
  if (m) {
    const a = +m[1].replace(/,/g, "");
    const b = +m[2].replace(/,/g, "");
    const p = b > 0 ? (100 * a) / b : 0;
    return (
      <div className="rounded-lg border border-border ag-surface px-3 py-2.5 text-center">
        <div className="text-sm font-bold text-foreground">
          {m[1]} <span className="text-[11px] font-normal text-muted-foreground">/ {m[2]} · {p.toFixed(1)}%</span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{ev.label}</div>
        <div className="mt-1.5 h-1 rounded-full bg-border overflow-hidden">
          <div className="h-full bg-primary" style={{ width: `${p.toFixed(1)}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border ag-surface px-3 py-2.5 text-center">
      <div className="text-sm font-bold text-foreground">{fmtNum(ev.value)}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{ev.label}</div>
    </div>
  );
}

function Soc2StatusBadge({ status }: { status?: string }) {
  const st = (status || "").toUpperCase();
  const cls =
    st === "EVIDENCED"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25"
      : st === "PARTIAL"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25"
      : "ag-surface text-muted-foreground border-border";
  const label = st === "NO_DATA" ? "NO DATA" : status || "—";
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border shrink-0", cls)}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {label}
    </span>
  );
}

function ToolBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-primary/[0.025] transition-colors"
    >
      {children}
    </button>
  );
}

const FALLBACK_FRAMEWORKS = [
  { id: "soc2", name: "SOC 2 (Trust Services Criteria)" },
  { id: "sox", name: "SOX — IT General Controls" },
];
// Per-framework pill: bold title + a smaller sub-heading for the descriptive part + an icon.
const FW_META: Record<string, { label: string; sub: string; icon: React.ReactNode }> = {
  soc2: { label: "SOC 2", sub: "Trust Services Criteria", icon: <ShieldCheck className="w-4 h-4 shrink-0" /> },
  sox: { label: "SOX", sub: "IT General Controls (financial reporting)", icon: <Landmark className="w-4 h-4 shrink-0" /> },
};

// Split a framework's full name into a title + sub-heading when we don't have hardcoded meta for it.
function fwParts(f: { id: string; name: string }): { label: string; sub: string; icon: React.ReactNode } {
  if (FW_META[f.id]) return FW_META[f.id];
  const paren = f.name.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (paren) return { label: paren[1].trim(), sub: paren[2].trim(), icon: <FileText className="w-4 h-4 shrink-0" /> };
  const dash = f.name.split(/\s+[—–-]\s+/);
  if (dash.length > 1) return { label: dash[0].trim(), sub: dash.slice(1).join(" — ").trim(), icon: <FileText className="w-4 h-4 shrink-0" /> };
  return { label: f.name, sub: "", icon: <FileText className="w-4 h-4 shrink-0" /> };
}

function ComplianceReport() {
  const { toast } = useToast();
  const [report, setReport] = useState<any>(null);
  const [metrics, setMetrics] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [fw, setFw] = useState("soc2");
  const [frameworks, setFrameworks] = useState<{ id: string; name: string }[]>(FALLBACK_FRAMEWORKS);

  const load = useCallback(async (framework: string) => {
    setFw(framework);
    setLoading(true);
    setError(null);
    try {
      const [rep, met] = await Promise.all([
        GatewayCisoApi.report(framework),
        GatewayCisoApi.complianceMetrics().catch(() => ({})),
      ]);
      setReport(rep);
      setMetrics(met || {});
      setOpen(new Set());
    } catch (e: any) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    GatewayCisoApi.frameworks()
      .then((l) => { if (Array.isArray(l) && l.length) setFrameworks(l); })
      .catch(() => {});
    load("soc2");
  }, [load]);

  const exportCsv = async () => {
    try {
      const csv = await GatewayCisoApi.reportCsv(fw);
      const blob = new Blob([typeof csv === "string" ? csv : JSON.stringify(csv)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fw}-evidence-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: "Evidence exported (CSV)" });
    } catch (e: any) {
      toast({ title: "Export failed", description: apiError(e), variant: "destructive" });
    }
  };

  // Print only the report: clone the report card to a body-level root; @media print isolates + expands it.
  const reportRef = useRef<HTMLDivElement>(null);
  const handlePrint = () => {
    const el = reportRef.current;
    if (!el) {
      window.print();
      return;
    }
    document.getElementById("ciso-print-root")?.remove();
    const root = document.createElement("div");
    root.id = "ciso-print-root";
    root.appendChild(el.cloneNode(true));
    document.body.appendChild(root);
    document.documentElement.classList.add("ciso-printing");
    const cleanup = () => {
      document.documentElement.classList.remove("ciso-printing");
      document.getElementById("ciso-print-root")?.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  const switcher = (
    <div className="inline-flex items-center gap-1 rounded-lg border border-border ag-surface p-1 w-fit">
      {frameworks.map((f) => {
        const active = f.id === fw;
        const { label, icon } = fwParts(f);
        return (
          <button
            key={f.id}
            onClick={() => load(f.id)}
            className={cn(
              "flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all",
              active ? "bg-primary/15 text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {icon}
            {label}
          </button>
        );
      })}
    </div>
  );

  if (loading) return <div className="space-y-4">{switcher}<Loading text="Generating evidence…" /></div>;
  if (error) return <div className="space-y-4">{switcher}<ErrorState what="compliance evidence report" msg={error} /></div>;

  const controls: any[] = report?.controls || [];
  const evidenced = controls.filter((x) => (x.status || "").toUpperCase() === "EVIDENCED").length;
  const families = Array.from(new Set(controls.map((x) => String(x.ref || "").split(".")[0]).filter(Boolean)));
  const gen = report?.generatedAt ? String(report.generatedAt).replace("T", " ").slice(0, 16) : "—";
  const days =
    report?.periodStart && report?.periodEnd
      ? Math.round((new Date(report.periodEnd).getTime() - new Date(report.periodStart).getTime()) / 86400000) + 1
      : null;
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const expandAll = () => setOpen(new Set(controls.map((_, i) => i)));
  const collapseAll = () => setOpen(new Set());

  const covNode: React.ReactNode =
    days != null ? (<>{days}<span className="text-sm font-medium text-muted-foreground"> days</span></>) : fmtNum(metrics["audit.period"]);
  // SOX leads its strip with post-processor data-classification tiles; other frameworks use access-decision tiles.
  const midTiles: { n: React.ReactNode; l: string }[] =
    fw === "sox"
      ? [
          { n: fmtNum(metrics["dataclass.classifications"]), l: "Responses Classified" },
          { n: fmtNum(metrics["dataclass.financial"]), l: "Financial Data" },
          { n: fmtNum(metrics["dataclass.restricted"]), l: "Restricted" },
        ]
      : [
          { n: fmtNum(metrics["audit.events"]), l: "Audit Events" },
          { n: fmtNum(metrics["decisions.total"]), l: "Access Decisions" },
          { n: fmtNum(metrics["decisions.denied"]), l: "Denials Enforced" },
        ];
  const strip: { n: React.ReactNode; l: string; good?: boolean }[] = [
    { n: controls.length, l: "Controls" },
    { n: evidenced, l: "Evidenced", good: true },
    ...midTiles,
    { n: covNode, l: "Coverage" },
  ];

  return (
    <div className="space-y-4">
      {/* framework sub-heading */}
      {switcher}
      {/* report actions — on their own line below the sub-heading, right-aligned */}
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <ToolBtn onClick={expandAll}>Expand all</ToolBtn>
        <ToolBtn onClick={collapseAll}>Collapse all</ToolBtn>
        <ToolBtn onClick={handlePrint}><Printer className="w-3.5 h-3.5" /> Print / PDF</ToolBtn>
        <ToolBtn onClick={exportCsv}><Download className="w-3.5 h-3.5" /> Export CSV</ToolBtn>
        <ToolBtn onClick={() => load(fw)}><RotateCcw className="w-3.5 h-3.5" /> Regenerate</ToolBtn>
      </div>
      <div ref={reportRef} className="ag-card p-6 space-y-5">
      {/* header */}
      <div className="flex items-start justify-between gap-6 flex-wrap border-b border-border pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Compliance Evidence Report</div>
          <h2 className="text-2xl font-bold text-foreground mt-1">{report?.framework || "SOC 2 — Trust Services Criteria"}</h2>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
          <dt className="text-muted-foreground uppercase tracking-wider">Tenant</dt>
          <dd className="font-mono text-foreground/85">{report?.tenant || "—"}</dd>
          <dt className="text-muted-foreground uppercase tracking-wider">Period</dt>
          <dd className="font-mono text-foreground/85">{report?.periodStart || "—"} → {report?.periodEnd || "—"}</dd>
          <dt className="text-muted-foreground uppercase tracking-wider">Generated</dt>
          <dd className="font-mono text-foreground/85">{gen}</dd>
          <dt className="text-muted-foreground uppercase tracking-wider">Controls</dt>
          <dd className="text-foreground/85">{controls.length} evaluated</dd>
        </dl>
      </div>

      {/* disclaimer */}
      {report?.disclaimer && (
        <div className="rounded-lg border border-border border-l-4 border-l-amber-500/70 bg-amber-500/5 px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold mb-1">Disclaimer</div>
          <p className="text-xs text-muted-foreground leading-relaxed">{report.disclaimer}</p>
        </div>
      )}

      {/* summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {strip.map((s) => (
          <div key={s.l} className="rounded-xl border border-border ag-surface px-4 py-3 text-center">
            <div className={cn("text-2xl font-bold leading-tight", s.good ? "text-emerald-600 dark:text-emerald-400" : "text-foreground")}>{s.n}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      {/* register bar */}
      <div>
        <h3 className="text-base font-bold text-foreground">Control Register</h3>
        <p className="text-xs text-muted-foreground">
          {controls.length} control(s){families.length ? ` across ${families.join(", ")}` : ""}
        </p>
      </div>

      {/* controls */}
      {controls.length === 0 ? (
        <div className="rounded-xl border border-border ag-surface p-6 text-center text-sm text-muted-foreground">
          No controls evaluated — the ledger may be empty for this period.
        </div>
      ) : (
        <div className="space-y-2.5">
          {controls.map((x, i) => {
            const isOpen = open.has(i);
            return (
              <div key={i} data-ciso-control className="rounded-xl border border-border ag-surface overflow-hidden portal-card-hover">
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                >
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold font-mono bg-primary/10 text-primary border border-primary/20 shrink-0">
                    {x.ref}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-foreground text-sm">{x.title}</span>{" "}
                    <span className="text-xs text-muted-foreground">{x.requirement}</span>
                  </span>
                  <Soc2StatusBadge status={x.status} />
                  <span data-ctl-chev className="shrink-0">
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </span>
                </button>
                <div data-ctl-body className={cn("border-t border-border px-4 py-4 space-y-3", !isOpen && "hidden")}>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">How satisfied</div>
                    <p className="text-sm text-foreground/85 leading-relaxed">{x.howSatisfied}</p>
                  </div>
                  {(x.evidence || []).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                      {(x.evidence || []).map((ev: any, j: number) => (
                        <EvidenceCell key={j} ev={ev} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* footer */}
      <div className="flex items-center justify-between gap-4 flex-wrap text-[11px] text-muted-foreground border-t border-border pt-3">
        <span>Framework: {report?.framework || "SOC 2"} · Source: gateway audit trail{fw === "sox" ? " & data classification" : ""}</span>
        <span className="font-mono">generated {report?.generatedAt || ""}</span>
      </div>
      </div>
    </div>
  );
}

type CisoTab = "dashboard" | "compliance";

export default function GatewayCisoPage() {
  const [tab, setTab] = useState<CisoTab>("dashboard");
  const tabs: { id: CisoTab; label: string; icon: React.ReactNode }[] = [
    { id: "dashboard", label: "Dashboard", icon: <Gauge className="w-4 h-4" /> },
    { id: "compliance", label: "Compliance", icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar ag-nav">
        <div className="cea-tabs ag-tabs" role="tablist" aria-label="CISO views">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn("cea-tab", tab === t.id && "active")}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <span className="ag-summary" title="Executive governance posture and compliance evidence from the gateway's decision ledger and data classification — read-only, real data.">
          <ShieldCheck aria-hidden="true" />
          <span>Read-only governance evidence</span>
        </span>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {tab === "dashboard" && <GatewayCisoDashboard />}
          {tab === "compliance" && <ComplianceReport />}
        </div>
      </div>
    </div>
  );
}
